// Base API service for HTTP requests
import { supabase } from '../lib/supabase';

/**
 * Determines the API base URL based on environment.
 * - PR previews: Derives backend URL from frontend URL pattern (checked FIRST)
 * - Local development: Uses the local backend on the same hostname, port 8000
 * - Production/staging: Uses REACT_APP_API_BASE_URL env var
 */
export function getApiBaseUrl(): string {
  // Detect Render PR preview FIRST (before env var, since env var is baked at build time)
  // Frontend: h2okay-app-frontend-pr-48.onrender.com
  // Backend:  h2okay-app-pr-48.onrender.com
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const prMatch = host.match(/^(.+)-frontend(-pr-\d+)\.onrender\.com$/);
    if (prMatch) {
      const baseName = prMatch[1]; // e.g., "h2okay-app"
      const prSuffix = prMatch[2]; // e.g., "-pr-48"
      return `https://${baseName}${prSuffix}.onrender.com/api`;
    }

    // Local dev must never accidentally call the deployed backend because
    // frontend/.env can contain production values from Render testing.
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.protocol}//${host}:8000/api`;
    }
  }

  // Check for explicit env var (production/staging)
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (envBaseUrl) {
    return envBaseUrl;
  }

  // Default for local development
  return '/api';
}

// Custom error classes
export class ApiException extends Error {
  constructor(message: string, public statusCode?: number, public isNetworkError = false, public detail?: unknown) {
    super(message);
    this.name = 'ApiException';
  }
}

export class ValidationException extends Error {
  constructor(message: string, public errors?: Array<{ field: string; message: string }>) {
    super(message);
    this.name = 'ValidationException';
  }
}

export class NetworkException extends ApiException {
  constructor(message: string = 'Network error occurred') {
    super(message, 0, true);
    this.name = 'NetworkException';
  }
}

interface RetryOptions {
  maxAttempts?: number;
  delayMs?: number;
  retryOn?: (error: Error) => boolean;
}

/**
 * Module-scoped X-Act-As user id. Set by ImpersonationContext when an
 * admin starts/stops acting-as a consultant. Kept outside React so it
 * can be read inside ``ApiService.request`` without a context dance.
 */
let _actAsUserId: string | null = null;

/** Set the X-Act-As header value sent on every API request (or clear with null). */
export function setActAsHeader(userId: string | null): void {
  _actAsUserId = userId;
}

/** Test-only: returns the current header value. */
export function _getActAsHeader(): string | null {
  return _actAsUserId;
}

export class ApiService {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? getApiBaseUrl();
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private shouldRetry(error: Error, attempt: number, maxAttempts: number): boolean {
    if (attempt >= maxAttempts) return false;

    // Retry on network errors
    if (error instanceof NetworkException) return true;

    // Retry on 5xx server errors
    if (error instanceof ApiException && error.statusCode && error.statusCode >= 500) return true;


    return false;
  }

  private async requestWithRetry<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOptions: RetryOptions = {}
  ): Promise<T> {
    const { maxAttempts = 3, delayMs = 1000, retryOn } = retryOptions;
    let lastError: Error = new ApiException('Request failed after retries');

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this.request<T>(endpoint, options);
      } catch (error) {
        lastError = error as Error;

        const safeMethod = ['GET', 'HEAD'].includes((options.method ?? 'GET').toUpperCase());
        const shouldRetryRequest = safeMethod && this.shouldRetry(lastError, attempt, maxAttempts) && (!retryOn || retryOn(lastError));

        if (!shouldRetryRequest || attempt === maxAttempts) {
          throw lastError;
        }

        // Exponential backoff: wait longer for each retry
        const backoffDelay = delayMs * Math.pow(2, attempt - 1);
        await this.delay(backoffDelay);
      }
    }

    throw lastError;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Inject the current Supabase access token on every request so the
    // backend can verify the caller. No session → no header → backend will
    // 401 and the AuthContext / PrivateRoute redirect to /login.
    const authHeader: Record<string, string> = {};
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) {
        authHeader.Authorization = `Bearer ${token}`;
      }
    } catch {
      /* no session yet — proceed without auth header */
    }
    // When an admin is acting-as a consultant, ImpersonationContext sets
    // this via setActAsHeader(). Backend ``get_current_user`` honors it
    // and flips the scoping subject; admin identity is preserved.
    if (_actAsUserId) {
      authHeader['X-Act-As'] = _actAsUserId;
    }
    // Lets the server tell current tabs from ones opened before a deploy.
    authHeader['X-Client-Build'] = process.env.REACT_APP_BUILD_ID || 'dev';

    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...authHeader,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        await this.handleError(response);
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      // Handle network errors (fetch failures)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkException('Unable to connect to the server. Please check your internet connection.');
      }

      // Handle other fetch API errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new NetworkException('Request was cancelled or timed out.');
      }

      // Re-throw known error types
      if (error instanceof ApiException || error instanceof ValidationException || error instanceof NetworkException) {
        throw error;
      }

      // Wrap unknown errors
      throw new ApiException(`Request failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private async handleError(response: Response): Promise<never> {
    let errorData: any;

    try {
      errorData = await response.json();
    } catch {
      errorData = { detail: 'Unknown error occurred' };
    }

    const detail = errorData.detail;
    const message = typeof detail === 'string' ? detail
      : typeof detail?.message === 'string' ? detail.message
      : typeof errorData.message === 'string' ? errorData.message
      : 'An error occurred';

    switch (response.status) {
      case 400:
        throw new ApiException(message, 400, false, detail);
      case 401:
        throw new ApiException('You are not authorized to perform this action.', 401, false, detail);
      case 403:
        throw new ApiException('You do not have permission to access this resource.', 403, false, detail);
      case 404:
        throw new ApiException(message, 404, false, detail);
      case 409:
        throw new ApiException(message, 409, false, detail);
      case 422: {
        // Pydantic/validation errors commonly return detail arrays
        if (Array.isArray(errorData.detail)) {
          const errors = errorData.detail.map((err: any) => {
            const loc = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : err.loc;
            return {
              field: typeof loc === 'string' ? loc : 'field',
              message: loc === 'version' ? 'Version must be 1–20 characters' : err.msg || 'Invalid value',
            };
          });

          const friendlyMessage =
            errors.length === 1 ? errors[0].message : 'Please correct the highlighted fields.';

          throw new ValidationException(friendlyMessage, errors);
        }

        if (errorData.errors) {
          throw new ValidationException(
            message,
            errorData.errors
          );
        }
        throw new ValidationException(message);
      }
      case 429:
        throw new ApiException('Too many requests. Please wait a moment and try again.', 429, false, detail);
      case 500:
        throw new ApiException('Internal server error. Please try again later.', 500, false, detail);
      case 502:
        throw new ApiException('Service unavailable. Please try again later.', 502, false, detail);
      case 503:
        throw new ApiException('Service temporarily unavailable. Please try again later.', 503, false, detail);
      case 504:
        throw new ApiException('Request timeout. Please try again.', 504, false, detail);
      default:
        throw new ApiException(`HTTP ${response.status} error: ${message}`, response.status, false, detail);
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>, retryOptions?: RetryOptions): Promise<T> {
    let url = endpoint;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });

      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`;
      }
    }

    return this.requestWithRetry<T>(url, { method: 'GET' }, retryOptions);
  }

  async post<T>(endpoint: string, data?: any, retryOptions?: RetryOptions): Promise<T> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, retryOptions);
  }

  async put<T>(endpoint: string, data?: any, retryOptions?: RetryOptions): Promise<T> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, retryOptions);
  }

  async patch<T>(endpoint: string, data?: any, retryOptions?: RetryOptions): Promise<T> {
    return this.requestWithRetry<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, retryOptions);
  }

  async delete<T>(endpoint: string, retryOptions?: RetryOptions): Promise<T> {
    return this.requestWithRetry<T>(endpoint, { method: 'DELETE' }, retryOptions);
  }

  /** Public base URL — used by services that must construct full URLs
   *  for non-JSON workflows (e.g. multipart upload to the lab-report
   *  upload-blob endpoint). */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /** Headers required to authenticate against the backend. Exposed so
   *  non-JSON workflows can attach them when bypassing ``request()``. */
  async authHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* no session */
    }
    if (_actAsUserId) headers['X-Act-As'] = _actAsUserId;
    headers['X-Client-Build'] = process.env.REACT_APP_BUILD_ID || 'dev';
    return headers;
  }
}

// Create singleton instance
export const apiService = new ApiService();
